import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AlertCircle, ArrowLeft, RefreshCw, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

/**
 * Página de error de pago
 *
 * Muestra información cuando el pago con Transbank falló
 */
export function PaymentErrorPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  // Obtener parámetros de la URL
  const message = searchParams.get("message");
  const error = searchParams.get("error");

  useEffect(() => {
    // Simular carga de datos
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const getErrorMessage = () => {
    if (message) return decodeURIComponent(message);
    if (error) return decodeURIComponent(error);
    return "Ocurrió un error durante el proceso de pago";
  };

  const getErrorDetails = () => {
    if (error === "REJECTED") {
      return "El pago fue rechazado por el banco. Verifica que tu tarjeta tenga fondos suficientes.";
    }
    if (error === "FAILED") {
      return "La transacción falló por un problema técnico. Intenta nuevamente.";
    }
    if (error === "EXPIRED") {
      return "La sesión de pago expiró. Inicia el proceso nuevamente.";
    }
    return "Verifica tu información de pago e intenta nuevamente.";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Procesando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver al Inicio
            </Button>
          </div>
        </div>
      </div>

      {/* Contenido Principal */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Mensaje de Error */}
          <Card className="mb-6 border-red-200 bg-red-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="h-8 w-8 text-red-600" />
                <div>
                  <h1 className="text-2xl font-bold text-red-800">
                    Error en el Pago
                  </h1>
                  <p className="text-red-600">{getErrorMessage()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detalles del Error */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Información del Error
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Tipo de Error
                  </label>
                  <Badge variant="destructive" className="mt-1">
                    {error || "Error General"}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Estado
                  </label>
                  <Badge variant="outline" className="mt-1">
                    Pago Fallido
                  </Badge>
                </div>
              </div>

              <Separator />

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h3 className="font-medium text-amber-800 mb-2">
                  ¿Qué puedes hacer?
                </h3>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Verifica que tu tarjeta tenga fondos suficientes</li>
                  <li>
                    • Asegúrate de que los datos de tu tarjeta sean correctos
                  </li>
                  <li>• Intenta con otra tarjeta si el problema persiste</li>
                  <li>• Contacta a tu banco si necesitas ayuda</li>
                </ul>
              </div>

              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <CreditCard className="h-4 w-4 text-blue-600" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800">
                    Transbank WebPay Plus
                  </p>
                  <p className="text-blue-600">{getErrorDetails()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Acciones */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => navigate("/checkout")}
              className="flex-1"
              size="lg"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Intentar Nuevamente
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              className="flex-1"
              size="lg"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al Inicio
            </Button>
          </div>

          {/* Información Adicional */}
          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              Si el problema persiste, contacta a nuestro soporte técnico
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Tu carrito se mantiene intacto para que puedas intentar nuevamente
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
